import I18n from 'I18n';
import { useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import styled from 'styled-components';
import { AIBadge } from 'ai-components-ui-library/components/informative/AIBadge';
import { AnimatedStepBody } from '../../animation/StepBodyAnimations';
import { getPostAccountCreationSteps } from '../../store/flow/selectors';
import { devices } from 'signup-constants/styles/viewports.styles';
import { StepTitle } from '../../views-components/StepTitle';
import { Step } from 'signup-constants/Step';
import { useCurrentStep } from '../../hooks/useCurrentStep';
import { getFlowConfig } from '../../flow-initial/getFlowConfig';
import { Content, StepCounterBadge } from 'signup-components/components/SidePanelLayout/SidePanelLayout';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { SidePanelCompanyDomainFooter } from './SidePanelCompanyDomainFooter';
import { TrackWhen, useExperiment } from '../../experiments/useExperiment';
import { ExperimentParameterGroup } from '../../experiments/ExperimentParameterGroup';
import { ExperimentKeys } from '../../experiments/ExperimentKeys';
import { SidePanelBaseViewFooter } from './SidePanelBaseViewFooter';
import { View } from 'foundations-components/components/View';
import BreezeErrorModal from './BreezeErrorModal';
import { useResponsiveness } from '../../responsiveness/ResponsivenessProvider';
import { SidePanelManageCookies } from 'signup-components/components/ManageCookies/SidePanelManageCookies';
import { useGTKMSingleFieldReturned } from '../../hooks/useGTKMSingleFieldReturned';
import { useShowErrorModal } from '../../hooks/useShowErrorModal';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const HeaderZeroMarginBottomSteps = [Step.HubletSelection, Step.CodeVerification];
const SidePanelBaseViewHeader = styled.header.withConfig({
  displayName: "SidePanelBaseView__SidePanelBaseViewHeader",
  componentId: "sc-1kzoqo1-0"
})(["margin-bottom:", ";@media ", "{", "}"], ({
  theme
}) => theme.space['600'], devices.desktop, props => HeaderZeroMarginBottomSteps.includes(props.$stepKey) && `margin-bottom: 0;`);
export const SidePanelBaseView = ({
  title,
  helperText,
  footer,
  children,
  stepKey,
  navMarker,
  addExperimentClassName = false,
  disableAnimation = false,
  nextButtonLabel,
  hideSidePanelNextButton = false
}) => {
  const {
    currentStep
  } = useCurrentStep();
  const step = stepKey || currentStep;
  const {
    isDesktop
  } = useResponsiveness();
  const {
    gtkmOneFieldReturned
  } = useGTKMSingleFieldReturned();
  const {
    showErrorModal
  } = useShowErrorModal();
  const subTitle = gtkmOneFieldReturned ? I18n.text(`views.${stepKey}.acq173OneFieldSubtitle`) : helperText;
  const postAccountCreation = useSelector(getPostAccountCreationSteps);
  const shouldShowProgressBadge = ![Step.LandingPage, Step.CodeVerification, Step.ResendEmail, Step.Password].includes(step);
  const {
    isAssignedToSpecificVariant: isAssignedToACQ172Variant
  } = useExperiment(ExperimentKeys.ACQ0172, TrackWhen.never);
  const isAssignedToACQ172VariantB = isAssignedToACQ172Variant(ExperimentParameterGroup.Variant_B);
  const {
    isAssignedToSpecificVariant: isAssignedToACQ173Variant
  } = useExperiment(ExperimentKeys.ACQ0173, TrackWhen.never);
  const isAssignedToACQ173VariantA = isAssignedToACQ173Variant(ExperimentParameterGroup.Variant_A);
  const isAssignedToACQ173VariantC = isAssignedToACQ173Variant(ExperimentParameterGroup.Variant_C);
  const isAssignedToACQ173VariantAorC = isAssignedToACQ173VariantA || isAssignedToACQ173VariantC;
  if (!step) {
    return null;
  }
  const isPostAccountCreationStep = postAccountCreation.includes(currentStep);
  const shouldUseWave = getFlowConfig().shouldUseWaveOnPostAccountCreation;
  const getStepTitle = () => {
    if (gtkmOneFieldReturned) {
      return /*#__PURE__*/_jsx(StepTitle, {
        message: `views.${step}.title.acq173OneField`,
        stepKey: step,
        updateStyling: !isPostAccountCreationStep
      });
    }
    return title || /*#__PURE__*/_jsx(StepTitle, {
      stepKey: step,
      updateStyling: !isPostAccountCreationStep
    });
  };
  const navMarkerName = navMarker || `${step}_VIEW`;
  const experimentSeleniumAttribute = addExperimentClassName ? {
    'data-selenium-experiment': 'EXPERIMENT'
  } : null;
  const showCompanyDomainFooterAI = step === Step.CompanyDomain && (isAssignedToACQ172VariantB || isAssignedToACQ173VariantAorC);
  const addTopMargin = [Step.CodeVerification, Step.ResendEmail, Step.Password].includes(step);
  return /*#__PURE__*/_jsxs(Content, {
    children: [!isDesktop && /*#__PURE__*/_jsx(SidePanelManageCookies, {
      isMobile: true
    }), /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      children: [showErrorModal ? /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(BreezeErrorModal, {}), /*#__PURE__*/_jsx(View, {
          blockSize: "9.6vh"
        })]
      }) : /*#__PURE__*/_jsx(View, {
        blockSize: "19.4vh",
        marginBlockStart: addTopMargin ? '1400' : '0'
      }), /*#__PURE__*/_jsxs(AnimatedStepBody, {
        dataStep: step,
        disableAnimation: disableAnimation,
        children: [/*#__PURE__*/_jsx(NavMarker, {
          name: navMarkerName
        }), /*#__PURE__*/_jsxs("div", Object.assign({
          "data-selenium": `${step}_VIEW`
        }, experimentSeleniumAttribute, {
          children: [/*#__PURE__*/_jsxs(Flex, {
            alignItems: "baseline",
            children: [shouldShowProgressBadge && /*#__PURE__*/_jsx(StepCounterBadge, {
              marginBlockEnd: "400"
            }), /*#__PURE__*/_jsx(Flex, {
              flexGrow: 1
            }), (gtkmOneFieldReturned || currentStep === Step.Review) && /*#__PURE__*/_jsx(AIBadge, {})]
          }), /*#__PURE__*/_jsxs(SidePanelBaseViewHeader
          // @ts-expect-error Expression produces a union type that is too complex to represent.
          , {
            $isPostAccountCreationStep: isPostAccountCreationStep && shouldUseWave,
            $stepKey: step,
            children: [getStepTitle(), subTitle && /*#__PURE__*/_jsx(Text, {
              as: "div",
              marginBlockEnd: currentStep === Step.Review ? '600' : '800',
              typography: "body-200",
              children: subTitle
            })]
          }), children, footer]
        }))]
      })]
    }), showCompanyDomainFooterAI && /*#__PURE__*/_jsx(SidePanelCompanyDomainFooter, {
      stepKey: step
    }), !showCompanyDomainFooterAI && /*#__PURE__*/_jsx(SidePanelBaseViewFooter, {
      hideSidePanelNextButton: hideSidePanelNextButton,
      label: nextButtonLabel,
      stepKey: step
    })]
  });
};